import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Package, LogOut, Mail, Phone, User, Calendar, Clock, CheckCircle2, CreditCard, Building2, Trash2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_LABEL = {
  pending: { label: 'En attente paiement', color: 'bg-amber-100 text-amber-700', icon: Clock },
  awaiting_transfer: { label: 'En attente du virement', color: 'bg-blue-100 text-blue-700', icon: Building2 },
  paid: { label: 'Payée', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  processing: { label: 'En préparation', color: 'bg-purple-100 text-purple-700', icon: Package },
  shipped: { label: 'Prête au retrait', color: 'bg-indigo-100 text-indigo-700', icon: Package },
  delivered: { label: 'Récupérée', color: 'bg-gray-100 text-gray-700', icon: CheckCircle2 },
  cancelled: { label: 'Annulée', color: 'bg-red-100 text-red-700', icon: Clock },
};

const MonCompte = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyOrderId, setBusyOrderId] = useState(null);

  const refresh = () =>
    axios.get(`${API}/auth/orders`).then((r) => setOrders(r.data));

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, []);

  const handleLogout = async () => {
    await logout();
    toast.success('Déconnexion réussie');
    navigate('/');
  };

  const handleCancel = async (order) => {
    if (!window.confirm(`Annuler la commande ${order.orderNumber} ?`)) return;
    setBusyOrderId(order._id);
    try {
      await axios.post(`${API}/orders/${order._id}/cancel`);
      toast.success('Commande annulée');
      await refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Annulation impossible");
    } finally {
      setBusyOrderId(null);
    }
  };

  const handleResume = async (order) => {
    setBusyOrderId(order._id);
    try {
      const { data } = await axios.post(`${API}/orders/${order._id}/resume`, {
        originUrl: window.location.origin,
      });
      if (data.type === 'stripe' && data.url) {
        window.location.href = data.url;
        return;
      }
      if (data.type === 'transfer') {
        navigate('/paiement-succes', {
          state: {
            type: 'transfer',
            order_number: data.order_number,
            amount: data.amount,
            iban: data.iban,
            bic: data.bic,
            bank: data.bank,
            holder: data.holder,
          },
        });
        return;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reprise impossible");
    } finally {
      setBusyOrderId(null);
    }
  };

  if (!user) return null;

  return (
    <div className="bg-gray-50 min-h-[80vh]">
      <section className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl font-bold mb-2">Mon espace client</h1>
          <p className="text-gray-300">Bonjour {user.name} 👋</p>
        </div>
      </section>

      <section className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Profile card */}
          <aside className="lg:col-span-1">
            <div className="bg-white rounded-lg border border-gray-200 p-6 sticky top-24" data-testid="account-profile">
              <h2 className="font-bold text-lg mb-4">Mes informations</h2>
              <ul className="space-y-3 text-sm">
                <li className="flex items-start gap-3">
                  <User className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-gray-500 text-xs">Nom</div>
                    <div className="font-medium">{user.name}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-gray-500 text-xs">Email</div>
                    <div className="font-medium break-all">{user.email}</div>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-gray-400 mt-0.5" />
                  <div>
                    <div className="text-gray-500 text-xs">Téléphone</div>
                    <div className="font-medium">{user.phone || '—'}</div>
                  </div>
                </li>
              </ul>
              <button
                onClick={handleLogout}
                data-testid="account-logout"
                className="mt-6 w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-100 rounded text-sm font-medium"
              >
                <LogOut className="w-4 h-4" /> Déconnexion
              </button>
            </div>
          </aside>

          {/* Orders */}
          <div className="lg:col-span-2">
            <h2 className="font-bold text-2xl mb-4 text-gray-900">Mes commandes</h2>
            {loading ? (
              <div className="text-gray-500">Chargement…</div>
            ) : orders.length === 0 ? (
              <div className="bg-white rounded-lg border border-gray-200 p-12 text-center text-gray-500">
                <Package className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                Aucune commande pour l'instant.
              </div>
            ) : (
              <div className="space-y-4" data-testid="account-orders-list">
                {orders.map((o) => {
                  const status = STATUS_LABEL[o.status] || STATUS_LABEL.pending;
                  const StatusIcon = status.icon;
                  const isCancellable = o.status === 'pending' || o.status === 'awaiting_transfer';
                  const isBusy = busyOrderId === o._id;
                  const resumeLabel =
                    o.paymentMethod === 'transfer' ? 'Voir le RIB' : 'Reprendre le paiement';
                  return (
                    <div key={o._id} className="bg-white rounded-lg border border-gray-200 p-5">
                      <div className="flex items-start justify-between gap-4 mb-3">
                        <div>
                          <div className="font-mono font-bold text-[#E42313]">{o.orderNumber}</div>
                          <div className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                            <Calendar className="w-3 h-3" />
                            {new Date(o.createdAt).toLocaleString('fr-FR')}
                          </div>
                        </div>
                        <span className={`text-xs px-3 py-1 rounded-full font-semibold flex items-center gap-1 ${status.color}`}>
                          <StatusIcon className="w-3 h-3" /> {status.label}
                        </span>
                      </div>
                      <div className="space-y-1 text-sm border-t pt-3 mb-3">
                        {o.items.map((it, i) => (
                          <div key={i} className="flex justify-between text-gray-700">
                            <span>{it.name} <span className="text-gray-400">×{it.quantity} ml</span></span>
                            <span className="font-medium">{(it.price * it.quantity).toFixed(2)} €</span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between items-center text-sm border-t pt-3">
                        <div className="text-gray-500 flex items-center gap-1">
                          {o.paymentMethod === 'transfer' ? (
                            <><Building2 className="w-4 h-4" /> Virement bancaire</>
                          ) : (
                            <><CreditCard className="w-4 h-4" /> Carte / PayPal</>
                          )}
                        </div>
                        <div className="font-bold text-lg">{o.totalAmount.toFixed(2)} €</div>
                      </div>
                      {isCancellable && (
                        <div className="flex gap-2 mt-4 pt-4 border-t border-gray-100">
                          <button
                            data-testid={`order-resume-${o._id}`}
                            onClick={() => handleResume(o)}
                            disabled={isBusy}
                            className="flex-1 bg-[#E42313] text-white px-4 py-2 rounded text-sm font-semibold hover:bg-[#C11A0F] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                          >
                            <RefreshCw className={`w-4 h-4 ${isBusy ? 'animate-spin' : ''}`} />
                            {isBusy ? 'Patientez…' : resumeLabel}
                          </button>
                          <button
                            data-testid={`order-cancel-${o._id}`}
                            onClick={() => handleCancel(o)}
                            disabled={isBusy}
                            className="px-4 py-2 border border-red-300 text-red-700 rounded text-sm font-semibold hover:bg-red-50 transition-colors flex items-center gap-2 disabled:opacity-50"
                            aria-label="Annuler la commande"
                          >
                            <Trash2 className="w-4 h-4" />
                            Annuler
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default MonCompte;
